import { memo, useCallback, useMemo } from 'react';
import { Dropdown, mergeClasses, Option } from '@fluentui/react-components';
import type { OptionOnSelectData, SelectionEvents } from '@fluentui/react-components';
import { useTranslation } from 'react-i18next';
import { useTaskRow, useProject, useGridStore, useIsGridReadOnly, useHighlightedTaskId, useIsTaskPinned } from '../state';
import { HoursInput } from './HoursInput';
import { TotalInput } from './TotalInput';
import { PinButton } from './PinButton';
import { useInitialData } from '../../../hooks/useInitialData';
import { useTimeGridStyles } from '../useTimeGridStyles';
import { usePinTask } from '../hooks/usePinTask';
import { resolveWorkTypeForApi } from '../utils/resolveWorkTypeForApi';

type TaskRowProps = {
    taskId: string;
    workType: string;
    dates: string[];
    style: React.CSSProperties;
};

export const TaskRowComponent = memo(function TaskRowComponent({ taskId, workType, dates, style }: TaskRowProps) {
    const task = useTaskRow(taskId, workType);
    const project = useProject(task?.projectId ?? '');
    const { updateTask, toggleTaskPinned } = useGridStore(s => ({ updateTask: s.updateTask, toggleTaskPinned: s.toggleTaskPinned }));
    const readOnly = useIsGridReadOnly();
    const highlightedTaskId = useHighlightedTaskId();
    const isHighlighted = highlightedTaskId === taskId;
    const isPinned = useIsTaskPinned(taskId, workType);
    const { mutate: pinTask } = usePinTask();
    const {
        data: {
            configuration: { showAllocations, workTypesEnabled },
        },
    } = useInitialData();
    const { t } = useTranslation('timesheet');
    const styles = useTimeGridStyles();

    const workTypes = useMemo(() => project?.workTypes ?? [], [project?.workTypes]);

    const selectedWorkTypeName = useMemo(() => {
        if (!task?.workType) return undefined;
        return workTypes.find(wt => wt.id === task.workType)?.name;
    }, [task?.workType, workTypes]);

    const onWorkTypeChange = useCallback(
        (_e: SelectionEvents, data: OptionOnSelectData) => {
            const value = data.optionValue;
            if (value) {
                updateTask(taskId, workType, { workType: value });
            }
        },
        [taskId, workType, updateTask],
    );

    const onTogglePin = useCallback(() => {
        const newPinned = !isPinned;
        toggleTaskPinned(taskId, workType);
        pinTask({ taskId, workTypeId: resolveWorkTypeForApi(workType), pinned: newPinned });
    }, [isPinned, taskId, workType, toggleTaskPinned, pinTask]);

    if (!task) return null;

    return (
        <div
            className={mergeClasses(styles.row, styles.rowTask, isHighlighted && styles.rowTaskHighlighted, showAllocations && styles.rowWithAllocation)}
            style={style}
            role="row"
        >
            <div className={mergeClasses(styles.cell, styles.cellTaskName, styles.stickyCol1)} role="gridcell">
                <span className={styles.taskIndent} />
                <span className={styles.ellipsisText} title={task.taskName}>
                    {task.taskName}
                </span>
                <PinButton pinned={isPinned} onToggle={onTogglePin} />
            </div>

            <div className={mergeClasses(styles.cell, styles.stickyCol2)} role="gridcell">
                {workTypesEnabled && workTypes.length > 0 ? (
                    <Dropdown
                        placeholder={t('worktype.noSelection')}
                        value={selectedWorkTypeName ?? ''}
                        selectedOptions={task.workType ? [task.workType] : []}
                        onOptionSelect={onWorkTypeChange}
                        disabled={readOnly}
                        style={{ minWidth: 0, width: '100%' }}
                        appearance="filled-lighter"
                    >
                        {workTypes.map(wt => (
                            <Option key={wt.id} value={wt.id}>
                                {wt.name ?? ''}
                            </Option>
                        ))}
                    </Dropdown>
                ) : (
                    <span />
                )}
            </div>

            {dates.map(date => (
                <HoursInput key={date} taskId={taskId} date={date} dates={dates} taskName={task.taskName} workType={workType} />
            ))}

            <TotalInput taskId={taskId} dates={dates} workType={workType} />

            {showAllocations && <div className={styles.cell} role="gridcell" />}
        </div>
    );
});
